import { BoatOfferCard, BoatOfferCardProps } from '@/components/boat-offer-card';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useBannerTracking, useBannerVisibilityTracker } from '@/hooks/use-banner-tracking';
import { getMediaUrl } from '@/lib/media';
import { type MediaItem } from '@/types';
import { BellRing } from 'lucide-react';
import React from 'react';

interface Banner {
    id: number;
    title: string;
    link_url: string | null;
    section: string;
    is_active: boolean;
    description: string | null;
    image_alt: string | null;
    desktop_image: MediaItem | null;
    mobile_image: MediaItem | null;
    views_count: number;
    clicks_count: number;
}

interface BoatResultsGridProps {
    boats: BoatOfferCardProps[];
    onFavorite?: (boatId: number) => void;
    onViewOffer?: (boatId: number) => void;
    type?: 'embarcacoes' | 'empresas';
    classifiedBanners?: Banner[];
    /** Abre o modal "Criar alerta" (CN-192) a partir do estado sem resultados; sem o callback a chamada não aparece. */
    onCreateAlert?: () => void;
}

interface TrackedResultsBannerProps {
    banner: Banner;
    bannerIndex: number;
    onBannerClick: (event: React.MouseEvent, bannerId: number | null, linkUrl: string | null) => Promise<void>;
    onBannerVisible: (bannerId: number, placement: string) => void;
}

function TrackedResultsBanner({ banner, bannerIndex, onBannerClick, onBannerVisible }: TrackedResultsBannerProps) {
    const bannerRef = useBannerVisibilityTracker<HTMLDivElement>(banner.id, `classified-results-${bannerIndex}`, onBannerVisible);

    return (
        <div ref={bannerRef}>
            <Card key={`banner-${banner.id}-${bannerIndex}`} className="h-[28rem] overflow-hidden rounded-xl p-0 sm:h-[25rem]">
                {banner.link_url ? (
                    <a
                        href={banner.link_url}
                        onClick={(event) => onBannerClick(event, banner.id, banner.link_url)}
                        className="block h-full w-full cursor-pointer"
                        aria-label={banner.image_alt || banner.title}
                    >
                        <img
                            src={getMediaUrl(banner.desktop_image, 'banner_desktop', getMediaUrl(banner.mobile_image, 'banner_mobile', '/ad-1.png'))}
                            alt={banner.image_alt || banner.title || 'Advertisement'}
                            className="h-full w-full object-cover"
                        />
                    </a>
                ) : (
                    <img
                        src={getMediaUrl(banner.desktop_image, 'banner_desktop', getMediaUrl(banner.mobile_image, 'banner_mobile', '/ad-1.png'))}
                        alt={banner.image_alt || banner.title || 'Advertisement'}
                        className="h-full w-full object-cover"
                    />
                )}
            </Card>
        </div>
    );
}

export const BoatResultsGrid: React.FC<BoatResultsGridProps> = ({
    boats,
    onFavorite,
    onViewOffer,
    type = 'embarcacoes',
    classifiedBanners = [],
    onCreateAlert,
}) => {
    const { trackBannerView, handleBannerClick } = useBannerTracking();

    if (boats.length === 0) {
        return (
            <div className="py-12 text-center">
                <div className="mb-4 text-4xl">🚤</div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    {type === 'empresas' ? 'Nenhuma empresa exatamente assim.' : 'Nenhuma embarcação exatamente assim agora.'}
                </h3>
                <p className="text-gray-600">Tente ajustar os filtros para ver mais resultados.</p>

                {type === 'embarcacoes' && onCreateAlert && (
                    <div className="mx-auto mt-6 flex max-w-md flex-col items-center gap-3 rounded-xl bg-white p-5 text-center sm:p-6">
                        <p className="text-base font-medium text-gray-900">Não achou? Crie um alerta e avisamos quando aparecer.</p>
                        <Button type="button" onClick={onCreateAlert} className="w-full bg-[#00D26A] text-gray-900 hover:bg-[#00bd5f] sm:w-auto">
                            <BellRing aria-hidden="true" className="h-4 w-4" />
                            Me avise quando aparecer uma assim
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    const renderItems = () => {
        const items: React.ReactNode[] = [];
        let bannerInsertionIndex = 0;

        boats.forEach((boat, index) => {
            items.push(
                <BoatOfferCard
                    key={`boat-${boat.id}`}
                    {...boat}
                    onFavorite={() => onFavorite?.(boat.id)}
                    onViewOffer={() => onViewOffer?.(boat.id)}
                />,
            );

            if ((index + 1) % 14 === 7 && classifiedBanners && classifiedBanners.length > 0 && type === 'embarcacoes') {
                const banner = classifiedBanners[bannerInsertionIndex % classifiedBanners.length];
                bannerInsertionIndex += 1;

                items.push(
                    <TrackedResultsBanner
                        key={`banner-${banner.id}-${index}`}
                        banner={banner}
                        bannerIndex={index}
                        onBannerClick={handleBannerClick}
                        onBannerVisible={trackBannerView}
                    />,
                );
            }
        });

        return items;
    };

    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">{renderItems()}</div>;
};
